import { service } from '@ember/service';
import ApplicationAdapter from './application';
import { addRequestLocaleToPagePayload } from 'admin-panel/utils/page-response-locale';
import config from 'admin-panel/config/environment';

import type SessionInfoService from 'admin-panel/services/session-info';
import type { TranslatedField } from 'admin-panel/constants/types';

const {
  api: { namespace },
} = config;

type UnlocalizedPagePayload = {
  page?: {
    content_translations?: unknown;
    title_translations?: unknown;
  };
};

export type UnlocalizedPageTranslations = {
  contentTranslations: TranslatedField;
  titleTranslations: TranslatedField;
};

export default class PageAdapter extends ApplicationAdapter {
  // Keep page detail loads isolated; one broken page makes coalesced /pages?ids[]=... fail the whole folder.
  coalesceFindRequests = false;

  @service
  declare sessionInfo: SessionInfoService;

  ajaxOptions(
    url: string,
    method: string,
    options: Parameters<ApplicationAdapter['ajaxOptions']>[2]
  ) {
    return super.ajaxOptions(url, method === 'PUT' ? 'PATCH' : method, options);
  }

  buildURL(...args: Parameters<ApplicationAdapter['buildURL']>) {
    const snapshot = args[2] as
      | { adapterOptions?: { unlocalized?: boolean } }
      | null
      | undefined;

    if (snapshot?.adapterOptions?.unlocalized) {
      return super.buildURL(...args);
    }

    return this.withContentLocale(
      super.buildURL(...args),
      this.sessionInfo.contentLocale.key
    );
  }

  rawQueryForLocale<Payload>(
    localeKey: string,
    query: Record<string, unknown>
  ) {
    const url = this.withContentLocale(
      super.buildURL('page', null, null, 'query', query),
      localeKey
    );

    return this.rawQuery<Payload>('page', query, url);
  }

  findUnlocalized<Payload>(id: string) {
    const url = super.buildURL('page', id, null, 'findRecord');

    return this.ajax(url, 'GET') as Promise<Payload>;
  }

  async findUnlocalizedTranslations(
    id: string
  ): Promise<UnlocalizedPageTranslations> {
    const payload = await this.findUnlocalized<UnlocalizedPagePayload>(id);
    const contentTranslations = translatedField(
      payload.page?.content_translations
    );
    const titleTranslations = translatedField(payload.page?.title_translations);

    if (!contentTranslations || !titleTranslations) {
      throw new Error('Unlocalized page response omitted translation maps');
    }

    return {
      contentTranslations,
      titleTranslations,
    };
  }

  private withContentLocale(url: string, localeKey: string) {
    return url.replace(`/${namespace}/`, `/${namespace}/${localeKey}/`);
  }

  handleResponse(...args: Parameters<ApplicationAdapter['handleResponse']>) {
    const payload = args[2] as
      | Parameters<typeof addRequestLocaleToPagePayload>[0]
      | undefined;
    const requestData = args[3] as { url?: string } | undefined;

    addRequestLocaleToPagePayload(payload, requestData?.url);

    return super.handleResponse(...args);
  }
}

function translatedField(value: unknown): TranslatedField | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;

  const entries = Object.entries(value);
  if (
    entries.some(
      ([, translation]) =>
        typeof translation !== 'string' && translation !== null
    )
  ) {
    return null;
  }

  return Object.fromEntries(entries) as TranslatedField;
}

declare module 'ember-data/types/registries/adapter' {
  export default interface AdapterRegistry {
    page: PageAdapter;
  }
}
